import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  Таблицата на тесен екран.
 *
 *  Хоризонтално плъзгаща се таблица е решението, което се взима, когато няма
 *  идея: на телефон човек не знае, че вдясно има още колони, и не ги вижда.
 *  Затова всеки ред става карта — същите данни, преподредени по важност за
 *  малък екран, с четими цели за пръст.
 *
 *  `WideOnly` / `NarrowOnly` държат двата вида един до друг: широкият екран
 *  запазва таблицата непроменена, тесният получава картите. Границата е
 *  `board` (900 px) — виж styles.css.
 * ─────────────────────────────────────────────────────────────────────────
 */

export function WideOnly({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('hidden board:block', className)}>{children}</div>;
}

export function NarrowOnly({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('board:hidden', className)}>{children}</div>;
}

/** Списък от карти вместо `<tbody>`. */
export function CardList({ children, className }: { children: ReactNode; className?: string }) {
  return <ul className={cn('divide-y divide-line', className)}>{children}</ul>;
}

export function CardListItem({
  children,
  className,
  onClick,
}: {
  children: ReactNode;
  className?: string;
  onClick?: () => void;
}) {
  return (
    <li className={cn('px-3 py-3', className)} onClick={onClick}>
      {children}
    </li>
  );
}

/**
 * Ред „етикет → стойност". Етикетът остава вляво и не се свива, стойността
 * е вдясно и се пренася — така числата стоят в една колона и се сравняват
 * с поглед надолу, както в таблицата.
 */
export function DataRow({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex items-baseline justify-between gap-3 py-1', className)}>
      <dt className="shrink-0 text-[11px] uppercase tracking-wide text-ink-faint">{label}</dt>
      <dd className="min-w-0 text-right text-sm text-ink">{children}</dd>
    </div>
  );
}

/** Празният списък — същият текст, който показва таблицата. */
export function CardListEmpty({ children }: { children: ReactNode }) {
  return <p className="px-3 py-10 text-center text-sm text-ink-dim">{children}</p>;
}
